/**
 * AssistContext — Global context for the embedded AI assistant.
 *
 * Provides:
 *  - Page context detection (auto-detects from route)
 *  - Open/close state for the assistant column (persistido)
 *  - Quick actions per page type
 *  - CANVAS-SHELL-1: estado do VUKZ Canvas (descriptor only) + API
 *    openCanvas / updateCanvas / selectCanvasReference /
 *    clearCanvasSelection / closeCanvas
 *
 * NÃO guarda conversa: thread/mensagens vivem em assistSession.js
 * (singleton por role, mount-once no painel). O estado threadId/messages
 * que existia aqui era morto e foi removido em CANVAS-SHELL-1.
 *
 * Consumed by: EmbeddedAssistShell (chat) + WorkspaceArea/MainWorkspace
 * (Layout.js — CANVAS-LAYOUT-2: o Canvas vive no centro, não no aside)
 */
import React, { createContext, useContext, useState, useMemo, useCallback, useEffect, useReducer, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useRBAC } from './RBACContext';
import { AssistFormBridgeProvider, useAssistFormBridge } from './AssistFormBridge';
import {
  canvasReducer, initialCanvasState, saveCanvasDescriptor,
  descriptorFromWorkspace, sameDescriptor,
} from '../components/assist/canvasState';

// Re-export: consumidores antigos podem importar daqui, mas páginas
// TESTADAS pelo jest devem importar de './AssistFormBridge' (sem router).
export { useAssistFormBridge };

const AssistContext = createContext();

export const useAssist = () => {
  const ctx = useContext(AssistContext);
  if (!ctx) throw new Error('useAssist must be used within AssistProvider');
  return ctx;
};

/** Versão tolerante: null fora do provider (VukzAssistPanel vive também em
 * /assist e widgets sem o shell embutido). */
export const useAssistOptional = () => useContext(AssistContext) || null;

/* ── Page context detection ─────────────────────────────────────────── */

const PAGE_CONTEXTS = [
  { pattern: /^\/assist$/,       type: 'vukz_assist',         label: 'VUKZ Assist' },
  { pattern: /^\/manager\/setup/, type: 'club_setup_wizard', label: 'Setup do Clube' },
  { pattern: /^\/manager\/claim/, type: 'official_data_claim', label: 'Vínculo Oficial' },
  { pattern: /^\/manager\/athletes\/reconcile/, type: 'athlete_reconciliation', label: 'Reconciliar Atletas' },
  { pattern: /^\/manager\/athletes\/bulk/, type: 'bulk_import', label: 'Importação em Massa' },
  { pattern: /^\/manager\/athletes\/new/, type: 'athlete_registration', label: 'Novo Atleta' },
  { pattern: /^\/manager\/athletes\/\d+/, type: 'athlete_profile', label: 'Perfil do Atleta' },
  { pattern: /^\/manager\/athletes/, type: 'athlete_management', label: 'Gestão de Atletas' },
  { pattern: /^\/manager/, type: 'manager_hub', label: 'Painel de Gestão' },
  { pattern: /^\/central/, type: 'central_hub', label: 'Central' },
  { pattern: /^\/feed/, type: 'social_feed', label: 'Feed' },
  { pattern: /^\/natacao/, type: 'swimming_hub', label: 'Natação' },
  { pattern: /^\/futebol/, type: 'football_hub', label: 'Futebol' },
  { pattern: /^\/jogador|^\/nadador/, type: 'athlete_detail', label: 'Perfil' },
  { pattern: /^\/clube/, type: 'club_detail', label: 'Clube' },
  { pattern: /^\/chat/, type: 'messaging', label: 'Mensagens' },
  { pattern: /^\/notificacoes/, type: 'notifications', label: 'Notificações' },
  { pattern: /^\/perfil|^\/arena-profile/, type: 'profile', label: 'Perfil' },
  { pattern: /^\/configuracoes/, type: 'settings', label: 'Configurações' },
];

const QUICK_ACTIONS = {
  club_setup_wizard: [
    { label: 'O que significa categoria?', prompt: 'Explique o que são categorias no esporte brasileiro (Mirim, Infantil, Juvenil, etc.)' },
    { label: 'Sugira estrutura para natação', prompt: 'Sugira uma estrutura esportiva inicial para um clube de natação com categorias Infantil a Adulto' },
    { label: 'Quais módulos ativar?', prompt: 'Quais módulos da plataforma são mais úteis para um clube que está começando?' },
    { label: 'Resumo do que preenchi', prompt: 'Faça um resumo do que já preenchi no wizard de setup do clube' },
  ],
  official_data_claim: [
    { label: 'Como funciona o vínculo?', prompt: 'Explique como funciona o processo de vínculo oficial de dados do clube' },
    { label: 'Status do meu pedido', prompt: 'Qual é o status atual do meu pedido de vínculo oficial?' },
    { label: 'O que posso fazer enquanto espero?', prompt: 'O que posso fazer na plataforma enquanto aguardo a aprovação do vínculo?' },
  ],
  athlete_management: [
    { label: 'Atletas vinculados?', prompt: 'Quais atletas já estão vinculados ao banco oficial?' },
    { label: 'Como cadastrar em massa?', prompt: 'Como faço cadastro em massa de atletas usando Excel?' },
    { label: 'Campos obrigatórios', prompt: 'Quais campos são obrigatórios no cadastro de atleta?' },
  ],
  central_hub: [
    { label: 'O que posso fazer aqui?', prompt: 'O que posso fazer na Central da plataforma?' },
    { label: 'Pendências do clube', prompt: 'Quais são as pendências atuais do meu clube?' },
    { label: 'Status do vínculo', prompt: 'Qual é o status atual do vínculo oficial do meu clube?' },
  ],
  _default: [
    { label: 'O que posso fazer?', prompt: 'O que posso fazer nesta página?' },
    { label: 'Ajuda rápida', prompt: 'Me dê uma orientação rápida sobre como usar esta parte da plataforma' },
  ],
};

/* ── Provider ───────────────────────────────────────────────────────── */

// C4d-1 (Workspace Shell): estado aberto/recolhido persistido no browser.
// Default: aberto em telas largas (>=1440px — workspace mantém largura
// funcional), recolhido abaixo disso e no mobile.
const ASSIST_OPEN_KEY = 'vukz_assist_open';

// CANVAS-LAYOUT-2 (02/10/2026): o Canvas NÃO mora mais no aside. O aside é
// SÓ o chat (coluna fixa à direita, 400px em >=1024); o Canvas ocupa a área
// central do Layout (MainWorkspace) substituindo visualmente a rota.
// `--vukz-aside-w` continua sendo a ÚNICA fonte de largura lida pelo Layout.
export const ASIDE_W = Object.freeze({ chat: 400 });

export function computeAsideWidth({ isOpen, viewportWidth }) {
  if (!isOpen) return 0;
  if (viewportWidth < 1024) return 0;
  return ASIDE_W.chat;
}

function useViewportWidth() {
  const read = () => { try { return window.innerWidth; } catch { return 1024; } };
  const [w, setW] = useState(read);
  useEffect(() => {
    const onResize = () => setW(read());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return w;
}

function initialAssistOpen() {
  try {
    const stored = window.localStorage.getItem(ASSIST_OPEN_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    return window.innerWidth >= 1440;
  } catch {
    return false;
  }
}

export const AssistProvider = ({ children }) => {
  const location = useLocation();
  const { activeRole, clubId } = useRBAC();

  const [isOpen, setIsOpen] = useState(initialAssistOpen);

  // Persistência do estado aberto/recolhido (apenas preferência de UI —
  // conversa vive em assistSession, não aqui).
  useEffect(() => {
    try { window.localStorage.setItem(ASSIST_OPEN_KEY, String(isOpen)); } catch { /* noop */ }
  }, [isOpen]);

  // ── CANVAS-SHELL-1: estado do Canvas (descriptor only) ─────────────
  const [canvas, dispatchCanvas] = useReducer(canvasReducer, undefined, initialCanvasState);
  useEffect(() => { saveCanvasDescriptor(canvas); }, [canvas]);
  const canvasRef = useRef(canvas);
  useEffect(() => { canvasRef.current = canvas; }, [canvas]);

  const openCanvas = useCallback((descriptor) => {
    dispatchCanvas({ type: 'open', descriptor });
    setIsOpen(true); // abrir o canvas implica Assist visível
  }, []);
  const updateCanvas = useCallback((patch) => dispatchCanvas({ type: 'update', patch }), []);
  const selectCanvasReference = useCallback((selection) => dispatchCanvas({ type: 'select', selection }), []);
  const clearCanvasSelection = useCallback(() => dispatchCanvas({ type: 'clearSelection' }), []);
  // CANVAS-LAYOUT-2 fix: "fechado pelo usuário" é intenção DESTA sessão e DESTE
  // conteúdo (kind+subject+revision). Vive só em memória — um descriptor
  // restaurado do sessionStorage após reload (isOpen:false) NÃO é dismiss:
  // o backend devolvendo o mesmo workspace deve abrir o Canvas.
  const dismissedRef = useRef(null);
  const closeCanvas = useCallback(() => {
    const c = canvasRef.current;
    dismissedRef.current = c.kind ? { kind: c.kind, subject: c.subject, params: c.params } : null;
    dispatchCanvas({ type: 'close' });
  }, []);
  const showCanvas = useCallback(() => { dismissedRef.current = null; dispatchCanvas({ type: 'show' }); }, []);

  // ── CANVAS-ENGINE-1: `data.workspace` é a autoridade sobre o Canvas ──
  // workspaceLive = último envelope do backend (completion/fields/blocks/
  // guard). NÃO persiste (sessionStorage só guarda o descriptor).
  const [workspaceLive, setWorkspaceLive] = useState(null);

  const applyWorkspace = useCallback((ws) => {
    if (ws === undefined) return; // backend sem flag → nada muda
    const cur = canvasRef.current;
    if (ws === null) {
      // Draft descartado/expirado → Canvas de rascunho (ou de 'Treino criado')
      // some. Wellness fica (o backend devolveria o workspace da thread se
      // ainda existisse).
      setWorkspaceLive(null);
      if (cur.kind === 'training_draft' || cur.kind === 'training_created' || cur.kind === 'training_edit') dispatchCanvas({ type: 'reset' });
      return;
    }
    const d = descriptorFromWorkspace(ws);
    setWorkspaceLive(ws);
    if (!d) return;
    const curD = cur.kind ? { kind: cur.kind, subject: cur.subject, params: cur.params } : null;
    if (sameDescriptor(curD, d)) {
      if (cur.isOpen) return;
      // fechado: só fica fechado se o usuário dispensou EXATAMENTE este
      // conteúdo nesta sessão e não há guard; restaurado-do-reload reabre.
      const dismissedSame = sameDescriptor(dismissedRef.current, d);
      if (dismissedSame && !ws.guard) return;
      dismissedRef.current = null;
      dispatchCanvas({ type: 'show' });
      setIsOpen(true);
      return;
    }
    // conteúdo novo (kind, alvo ou revision) → abre/atualiza
    dismissedRef.current = null;
    dispatchCanvas({ type: 'open', descriptor: d });
    setIsOpen(true);
  }, []);

  // Ponte Canvas → chat: o painel registra seu `send`; o Canvas responde ao
  // switch guard (continuar/revisar/descartar) como fala normal do coach.
  const senderRef = useRef(null);
  const registerCanvasSender = useCallback((fn) => { senderRef.current = fn || null; }, []);
  const sendFromCanvas = useCallback((text) => {
    if (typeof senderRef.current === 'function' && text) {
      return senderRef.current(text, { source: 'canvas' });
    }
    return null;
  }, []);

  const viewportWidth = useViewportWidth();
  // CANVAS-LAYOUT-2: 'column' (chat fixo à direita, workspace recua) | 'drawer' (<1024)
  const layoutMode = viewportWidth >= 1024 ? 'column' : 'drawer';
  const asideWidth = computeAsideWidth({ isOpen, viewportWidth });
  useEffect(() => {
    try { document.documentElement.style.setProperty('--vukz-aside-w', `${asideWidth}px`); } catch { /* noop */ }
  }, [asideWidth]);
  // BUILDER-FOOTER-1: o FAB (fixed bottom-6 right-6, 56px) existe quando o
  // chat está recolhido. Rodapés sticky de página (Builder) reservam esse
  // espaço à direita lendo --vukz-fab-w — sem importar este contexto.
  useEffect(() => {
    try { document.documentElement.style.setProperty('--vukz-fab-w', isOpen ? '0px' : '80px'); } catch { /* noop */ }
  }, [isOpen]);

  // Detect page context from route
  const pageContext = useMemo(() => {
    const match = PAGE_CONTEXTS.find(p => p.pattern.test(location.pathname));
    return match || { type: 'unknown', label: 'Página' };
  }, [location.pathname]);

  // Quick actions for current page
  const quickActions = useMemo(() => {
    return QUICK_ACTIONS[pageContext.type] || QUICK_ACTIONS._default;
  }, [pageContext.type]);

  const toggle = useCallback(() => setIsOpen(prev => !prev), []);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo(() => ({
    // State
    isOpen,
    pageContext,
    quickActions,
    currentRoute: location.pathname,

    // Derived
    role: activeRole || 'ATLETA',
    clubId: clubId || null,

    // Layout (CANVAS-LAYOUT-2)
    layoutMode,      // 'column' | 'drawer'
    asideWidth,      // px — espelhado em --vukz-aside-w (só chat)

    // Canvas
    canvas,
    openCanvas,
    updateCanvas,
    selectCanvasReference,
    clearCanvasSelection,
    closeCanvas,
    showCanvas,
    // CANVAS-ENGINE-1
    workspaceLive,
    applyWorkspace,
    registerCanvasSender,
    sendFromCanvas,

    // Actions
    toggle,
    open,
    close,
  }), [isOpen, pageContext, quickActions, location.pathname, activeRole, clubId,
       layoutMode, asideWidth, canvas,
       openCanvas, updateCanvas, selectCanvasReference, clearCanvasSelection, closeCanvas, showCanvas,
       workspaceLive, applyWorkspace, registerCanvasSender, sendFromCanvas,
       toggle, open, close]);

  return (
    <AssistContext.Provider value={value}>
      <AssistFormBridgeProvider>
        {children}
      </AssistFormBridgeProvider>
    </AssistContext.Provider>
  );
};
